import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Smartphone, Printer, BookOpen } from "lucide-react";
import api, { eur } from "../api";
import { usePageSeo, JsonLd, SITE_URL } from "../components/common";

const FEATURES = [
  "78 fiches A4 illustrées — une par carte du Tarot de Marseille (22 Arcanes Majeurs + 56 Arcanes Mineurs)",
  "Pour chaque carte : mots-clés, significations, amour, travail & argent, spirituel, temporalité, oui/non, conseil de Noëmie",
  "Interprétation à l'endroit et à l'envers",
  "+ 9 fiches pratiques (tirages, méthodes, nombres, rituel)",
  "+ un Kit Intuitif de 13 bonus pour développer ton intuition et pratiquer en autonomie",
];

export default function GuideTarot() {
  usePageSeo({
    title: "Le Grimoire Visuel du Tarot",
    description: "Le Grimoire Visuel du Tarot (Tarot de Marseille) : 78 fiches A4 illustrées, 9 fiches pratiques et un Kit Intuitif de 13 bonus. Arrête d'apprendre le Tarot, commence à le lire.",
    path: "/guide-du-tarot",
  });
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [f, setF] = useState({ first_name: "", last_name: "", email: "" });

  useEffect(() => { api.get("/offers/config").then((r) => setCfg(r.data.guide)).catch(() => {}); }, []);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const buy = async (e) => {
    e.preventDefault();
    if (!f.email) { toast.error("Merci d'indiquer ton email."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/offers/guide/checkout", { ...f, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Une erreur est survenue.");
      setLoading(false);
    }
  };

  return (
    <div className="home-dark min-h-[80vh]">
      {cfg && (
        <JsonLd data={{
          "@context": "https://schema.org", "@type": "Product",
          name: cfg.title || "Le Grimoire Visuel du Tarot",
          description: cfg.description || "", brand: { "@id": `${SITE_URL}/#org` },
          url: `${SITE_URL}/guide-du-tarot`,
          offers: { "@type": "Offer", priceCurrency: "EUR",
            price: ((cfg.current_price_cents || 0) / 100).toFixed(2),
            availability: cfg.active ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
            url: `${SITE_URL}/guide-du-tarot` },
        }} />
      )}
      <div className="container-x py-12 sm:py-20 fade-up">
        <div className="grid lg:grid-cols-5 gap-10 lg:gap-14">
          <div className="lg:col-span-3 space-y-7">
            {cfg?.promo_on && (
              <div className="inline-flex items-center gap-2 rounded-full bg-[#D4AF37] text-[#12051F] px-4 py-1.5 text-sm font-bold" data-testid="guide-promo-badge">
                OFFRE DE LANCEMENT — {eur(cfg.price_promo_cents)} au lieu de {eur(cfg.price_normal_cents)}
              </div>
            )}
            <h1 className="font-serif text-4xl sm:text-5xl font-medium text-[#F9F5EE]">
              {cfg?.title || "Le Grimoire Visuel du Tarot"}
            </h1>
            <p className="font-script text-2xl sm:text-3xl text-[#DFC07B]">
              Arrête d'apprendre le Tarot. Commence à le lire.
            </p>
            <div className="gold-divider w-28" />
            <p className="text-[#E7DCEA] leading-relaxed text-lg">{cfg?.description}</p>
            <div className="card-soft p-6 sm:p-7">
              <div className="eyebrow mb-4">Ce que contient le guide</div>
              <ul className="space-y-2.5">
                {FEATURES.map((t) => (
                  <li key={t} className="flex items-start gap-2.5 text-sm text-[#5A3A4B]">
                    <Check size={17} className="text-[#8a6a2f] shrink-0 mt-0.5" /> {t}
                  </li>
                ))}
              </ul>
            </div>
            <div className="grid grid-cols-3 gap-4 text-center">
              {[[BookOpen, "Numérique"], [Printer, "Imprimable"], [Smartphone, "Tel / tablette / ordi"]].map(([Icon, t]) => (
                <div key={t} className="rounded-2xl border border-[#D4AF37]/25 bg-[#2B0D46]/40 p-4 text-sm text-[#E7DCEA]">
                  <Icon size={18} className="mx-auto text-[#DFC07B] mb-2" />{t}
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-2">
            <form onSubmit={buy} className="card-soft p-6 sm:p-7 lg:sticky lg:top-24 space-y-4" data-testid="guide-form">
              <div className="text-center">
                {cfg?.promo_on && (
                  <span className="block text-sm text-[#a58f92] line-through">{eur(cfg.price_normal_cents)}</span>
                )}
                <div className="font-serif text-5xl font-semibold text-[#5A3A4B]" data-testid="guide-price">
                  {cfg ? eur(cfg.current_price_cents) : "—"}
                </div>
                <p className="text-xs text-[#7A5D58] mt-1">Paiement unique · accès dans ton espace</p>
              </div>
              <div className="gold-divider my-2" />
              <div className="grid grid-cols-2 gap-3">
                <div><label className="field-label">Prénom</label><input className="field-input" value={f.first_name} onChange={set("first_name")} data-testid="guide-first" /></div>
                <div><label className="field-label">Nom</label><input className="field-input" value={f.last_name} onChange={set("last_name")} data-testid="guide-last" /></div>
              </div>
              <div><label className="field-label">Email</label><input required type="email" className="field-input" value={f.email} onChange={set("email")} data-testid="guide-email" /></div>
              <button type="submit" disabled={loading} className="btn-primary w-full disabled:opacity-50" data-testid="guide-buy">
                {loading ? "Redirection…" : "Acheter le guide"}
              </button>
              <p className="text-xs text-[#a58f92] text-center leading-relaxed">
                Paiement sécurisé par Stripe. Après ton achat, ton guide est disponible dans « Mes contenus » de ton espace.
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
